package co.maytes.standupbot.dashboard.components.widgets.svg

import androidx.compose.runtime.Composable
import kotlinx.browser.document
import org.jetbrains.compose.web.attributes.AttrsScope
import org.jetbrains.compose.web.dom.ContentBuilder
import org.jetbrains.compose.web.dom.ElementBuilder
import org.jetbrains.compose.web.dom.TagElement
import org.w3c.dom.Element
import org.w3c.dom.svg.SVGElement

private const val SVG_NS = "http://www.w3.org/2000/svg"

private class SvgElementBuilder<T : Element>(private val tagName: String) : ElementBuilder<T> {
    private val el: Element by lazy { document.createElementNS(SVG_NS, tagName) }

    @Suppress("UNCHECKED_CAST")
    override fun create(): T = el.cloneNode() as T
}

private val svgBuilders = mutableMapOf<String, ElementBuilder<*>>()

@Suppress("UNCHECKED_CAST")
private fun <T : Element> svgBuilder(tag: String): ElementBuilder<T> =
    svgBuilders.getOrPut(tag) { SvgElementBuilder<T>(tag) } as ElementBuilder<T>

@Composable
fun Svg(
    attrs: (AttrsScope<SVGElement>.() -> Unit)? = null,
    content: ContentBuilder<SVGElement>? = null,
) = TagElement(svgBuilder<SVGElement>("svg"), attrs, content)

@Composable
fun SvgGroup(
    attrs: (AttrsScope<Element>.() -> Unit)? = null,
    content: ContentBuilder<Element>? = null,
) = TagElement(svgBuilder<Element>("g"), attrs, content)

@Composable
fun SvgRect(attrs: (AttrsScope<Element>.() -> Unit)? = null) =
    TagElement<Element>(svgBuilder("rect"), attrs, null)

@Composable
fun SvgLine(attrs: (AttrsScope<Element>.() -> Unit)? = null) =
    TagElement<Element>(svgBuilder("line"), attrs, null)

@Composable
fun SvgCircle(attrs: (AttrsScope<Element>.() -> Unit)? = null) =
    TagElement<Element>(svgBuilder("circle"), attrs, null)

@Composable
fun SvgPath(attrs: (AttrsScope<Element>.() -> Unit)? = null) =
    TagElement<Element>(svgBuilder("path"), attrs, null)

@Composable
fun SvgPolyline(attrs: (AttrsScope<Element>.() -> Unit)? = null) =
    TagElement<Element>(svgBuilder("polyline"), attrs, null)

@Composable
fun SvgPolygon(attrs: (AttrsScope<Element>.() -> Unit)? = null) =
    TagElement<Element>(svgBuilder("polygon"), attrs, null)

@Composable
fun SvgText(
    text: String,
    attrs: (AttrsScope<Element>.() -> Unit)? = null,
) = TagElement<Element>(svgBuilder("text"), attrs) {
    org.jetbrains.compose.web.dom.Text(text)
}

@Composable
fun SvgDefs(content: ContentBuilder<Element>? = null) =
    TagElement<Element>(svgBuilder("defs"), null, content)

@Composable
fun SvgLinearGradient(
    id: String,
    attrs: (AttrsScope<Element>.() -> Unit)? = null,
    content: ContentBuilder<Element>? = null,
) = TagElement<Element>(
    svgBuilder("linearGradient"),
    {
        attr("id", id)
        attrs?.invoke(this)
    },
    content,
)

@Composable
fun SvgStop(offsetPct: Int, color: String, opacity: Double = 1.0) =
    TagElement<Element>(svgBuilder("stop"), {
        attr("offset", "$offsetPct%")
        attr("stop-color", color)
        attr("stop-opacity", opacity.toString())
    }, null)
